import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../components/AuthProvider';
import { homeRoute, irAlDestino } from '../lib/homeRoute';
import { CLAVE_GENERICA, LARGO_MINIMO, validarClaveNueva } from '../domain/passwordPolicy';
import CampoClave from '../components/CampoClave';

/**
 * Pantalla donde cada persona se crea SU clave.
 *
 * Obligatoria la primera vez (`profiles.must_change_password`, migración 0015) y disponible
 * siempre después desde el menú. La razón está a la vista en la pantalla: los accesos se
 * entregan todos con la misma clave, y mientras eso siga así cualquiera que sepa el
 * documento de una compañera puede entrar como ella y marcarle la asistencia.
 */

/** Traduce el rechazo de Supabase (que llega en inglés) a algo accionable en campo. */
function mensajeDeAuth(bruto: string): string {
  if (/different from the old password/i.test(bruto)) {
    return 'Esa ya es tu clave actual. Escribe una distinta.';
  }
  if (/weak|easy to guess|pwned|leaked/i.test(bruto)) {
    return 'Esa clave es muy fácil de adivinar. Prueba con otra que no sea obvia.';
  }
  if (/at least|should be longer|6 characters/i.test(bruto)) {
    return `La clave debe tener al menos ${LARGO_MINIMO} caracteres.`;
  }
  if (/session|jwt|token|expired/i.test(bruto)) {
    return 'Tu sesión venció. Vuelve a ingresar y cambia tu clave de nuevo.';
  }
  return 'No se pudo cambiar la clave. Revisa tu conexión e inténtalo de nuevo.';
}

export default function CambiarClave() {
  const { user, roles, mustChangePassword, marcarClaveCambiada } = useAuth();
  const navigate = useNavigate();
  const [nueva, setNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [listo, setListo] = useState(false);

  const obligatorio = mustChangePassword === true;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const revision = validarClaveNueva(nueva, confirmacion, CLAVE_GENERICA);
    if (!revision.ok) {
      setError(revision.error);
      return;
    }

    setGuardando(true);
    try {
      const { error: authError } = await supabase.auth.updateUser({ password: nueva });
      if (authError) {
        setError(mensajeDeAuth(authError.message ?? ''));
        return;
      }

      // La clave YA cambió. Apagar la marca es contabilidad: si falla (la 0015 todavía no
      // está aplicada y la columna no existe, 42703), no se le dice nada a la persona —
      // sería mentirle diciendo que no se guardó algo que sí se guardó. Sin la columna,
      // el guard tampoco bloquea, así que no queda atrapada.
      if (user) {
        await supabase.from('profiles').update({ must_change_password: false }).eq('id', user.id);
      }
      marcarClaveCambiada?.();
      setListo(true);
    } catch {
      setError('No se pudo cambiar la clave. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setGuardando(false);
    }
  }

  async function cerrarSesion() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  if (listo) {
    return (
      <Marco>
        <h1 className="text-xl font-bold text-gray-900">Listo: esta clave es tuya</h1>
        <p className="mt-2 text-sm text-gray-600">
          La próxima vez ingresa con tu documento y <strong>esta</strong> clave. Nadie más la
          conoce, así que guárdala bien: si la pierdes, hay que crearte una nueva.
        </p>
        <button
          type="button"
          onClick={() => irAlDestino(homeRoute(roles), navigate)}
          className="mt-5 w-full rounded bg-blue-600 py-2 text-sm font-medium text-white"
        >
          Continuar
        </button>
      </Marco>
    );
  }

  return (
    <Marco>
      <h1 className="text-xl font-bold text-gray-900">Cambiar mi clave</h1>
      {obligatorio ? (
        <p className="mb-4 mt-2 rounded bg-amber-50 p-3 text-sm text-amber-900">
          Por seguridad, crea tu propia clave antes de empezar. Nadie más va a conocerla — ni tu
          coordinador.
        </p>
      ) : (
        <p className="mb-4 mt-2 text-sm text-gray-500">
          Escribe tu clave nueva. Empieza a valer de inmediato.
        </p>
      )}

      <form onSubmit={onSubmit}>
        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="nueva">
          Clave nueva
        </label>
        <CampoClave
          id="nueva"
          autoComplete="new-password"
          required
          value={nueva}
          onChange={setNueva}
        />
        <ul className="mb-3 mt-1 list-disc pl-5 text-xs text-gray-500">
          <li>Al menos {LARGO_MINIMO} caracteres.</li>
          <li>Que no sea {CLAVE_GENERICA}: esa la conoce todo el equipo.</li>
        </ul>

        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="confirmacion">
          Repite la clave nueva
        </label>
        {/* Cada campo trae su propio ojito (`CampoClave`). Antes había UN enlace de texto que
            destapaba los dos a la vez; el ojito dentro de la casilla es donde la gente lo busca,
            y por campo permite comprobar la primera sin destapar la segunda. Sin esto, en un
            celular se escribe a ciegas dos veces y el error llega tarde. */}
        <CampoClave
          id="confirmacion"
          autoComplete="new-password"
          required
          value={confirmacion}
          onChange={setConfirmacion}
          className="mb-4"
        />

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={guardando}
          className="w-full rounded bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {guardando ? 'Guardando…' : 'Guardar mi clave'}
        </button>
      </form>

      {/* Volver, SOLO cuando el cambio no es obligatorio. Quien entra aquí por su cuenta a
          cambiar la clave y se arrepiente no tenía más salida que cerrar sesión: el único botón
          era ese. En el modo obligatorio no se ofrece, porque volver es justamente lo que esta
          pantalla existe para impedir. */}
      {!obligatorio && (
        <button
          type="button"
          onClick={() => irAlDestino(homeRoute(roles), navigate, false)}
          className="mt-4 w-full rounded border px-3 py-2 text-sm font-medium text-gray-700"
        >
          Volver
        </button>
      )}

      {/* Salida siempre disponible: en el modo obligatorio esta pantalla es la única puerta,
          y quedar encerrado sin poder ni salir sería peor que el problema que resuelve. */}
      <button
        type="button"
        onClick={cerrarSesion}
        className="mt-2 w-full rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
      >
        Cerrar sesión
      </button>
    </Marco>
  );
}

function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow">{children}</div>
    </div>
  );
}
